/** Packages — unified package store list (filterable by package_type). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { MoreHorizontal, Pencil, Plus, Trash2 } from "@/components/ui/icon";
import { PACKAGE_TYPES, type Package } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { StatusBadge } from "@/components/app/StatusBadge";

import {
  DataTable,
  PageHeader,
  SearchBox,
  type RefineTableCore,
} from "../projects/_shared";
import { humanize } from "../projects/_form";

/**
 * Packages list. `package_type` drives a Select-backed equality filter so the
 * single unified store renders skills / contours / bets / frames / principles.
 * The dedicated Bets/Frames/Principles routes (owned by D4) are separate
 * type-scoped views over the same `/api/packages` store.
 */
export function PackageList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deletePackage } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Package | null>(
    null,
  );
  const [typeFilter, setTypeFilter] = React.useState<string>("");

  const columns = useMemo<ColumnDef<Package, unknown>[]>(
    () => [
      { id: "name", accessorKey: "name", header: "Name" },
      {
        id: "package_type",
        accessorKey: "package_type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="outline">{humanize(row.original.package_type)}</Badge>
        ),
      },
      {
        id: "target_level",
        accessorKey: "target_level",
        header: "Target Level",
        cell: ({ row }) => humanize(row.original.target_level),
      },
      {
        id: "maturity_stage",
        accessorKey: "maturity_stage",
        header: "Maturity",
        cell: ({ row }) => (
          <Badge variant="secondary">
            {humanize(row.original.maturity_stage)}
          </Badge>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => go({ to: editUrl("packages", row.original.id) })}
            onShow={() => go({ to: showUrl("packages", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Package, HttpError>({
    columns,
    refineCoreProps: {
      resource: "packages",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "name", order: "asc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: {
        field: string;
        operator: "contains" | "eq";
        value: string;
      }[],
    ) => void;
  };
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    const handle = setTimeout(() => {
      const filters: {
        field: string;
        operator: "contains" | "eq";
        value: string;
      }[] = [{ field: "q", operator: "contains", value: search }];
      if (typeFilter) {
        filters.push({
          field: "package_type",
          operator: "eq",
          value: typeFilter,
        });
      }
      core.setFilters(filters);
    }, 300);
    return () => clearTimeout(handle);
  }, [search, typeFilter, core]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Packages"
        description="The unified store: skills, contours, bets, frames, and principles."
        action={
          <Button onClick={() => go({ to: createUrl("packages") })}>
            <Plus className="mr-2 h-4 w-4" /> New Package
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search packages…"
        />
        <Select
          value={typeFilter || "__all__"}
          onValueChange={(v) => setTypeFilter(v === "__all__" ? "" : v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {PACKAGE_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {humanize(t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No packages in this view yet."
        onRowClick={(p) => go({ to: showUrl("packages", p.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete package?"
        description={
          <>
            This removes{" "}
            <span className="font-medium">{pendingDelete?.name}</span> from the
            store. This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deletePackage({ resource: "packages", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onEdit,
  onShow,
  onDelete,
}: {
  onEdit: () => void;
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PackageList;
